<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.evaluation_criteria', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.update" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-12 md:gap-6">
                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                required
                                id="title"
                                v-model="form.title"
                                name="title"
                                type="text"
                                max="191"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.title" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-12">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.description')" for="description" />
                            <FormTextarea id="description" v-model="form.description" name="description" class="mt-1" />
                            <FormError :value="form.errors.description" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.stages', 1)" for="stage_id" />
                            <FormSelect id="stage_id" v-model="form.stage_id" :options="options.stages" class="mt-1" />
                            <FormError :value="form.errors.stage_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids">
                            <SelectAllNoneButtons
                                :max-count="filteredGradeLevels.length"
                                :selected-count="form.grade_level_ids.length"
                                @select-all="selectAllGradeLevels()"
                                @select-none="form.grade_level_ids = []"
                            />
                        </FormLabel>
                        <div v-if="filteredGradeLevels.length === 0" class="text-gray-600">
                            {{ $t('messages.select') }}: {{ $tc('messages.stages', 1) }}
                        </div>
                        <div class="mt-1">
                            <label v-for="grade_level in filteredGradeLevels" class="mr-1" :key="grade_level.value">
                                <input
                                    v-model="form.grade_level_ids"
                                    name="grade_level_ids"
                                    :value="grade_level.value"
                                    type="checkbox"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                                {{ grade_level.text }}
                            </label>
                        </div>
                        <FormError :value="form.errors.grade_level_ids" class="mt-2" />
                    </div>
                    <div class="sm:col-span-12">
                        <FormLabel :value="$t('messages.dimensions')" for="dimensions" />
                        <FormError :value="form.errors.dimension_ids" class="mt-2" />
                        <div class="md:grid md:grid-cols-12 md:gap-6">
                            <div class="sm:col-span-4">
                                <div v-if="filteredCategories.length === 0" class="text-gray-600">
                                    {{ $t('messages.select') }}: {{ $tc('messages.stages', 1) }}
                                </div>
                                <CategoryDimensionsSelector
                                    :categories="filteredCategories"
                                    v-else
                                    v-model="form.dimension_ids"
                                />
                            </div>
                            <CategoryDimensionsSelectorSummary
                                class="sm:col-span-8"
                                :categories="filteredCategories"
                                :model-value="form.dimension_ids"
                                @remove-dimension="removeDimension"
                            />
                        </div>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                    {{ $t('messages.save') }}
                    <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                </AppButton>
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import CategoryDimensionsSelector from '@/components/CategoryDimensionsSelector.vue';
import CategoryDimensionsSelectorSummary from '@/components/CategoryDimensionsSelectorSummary.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';

export default {
    components: {
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        AppButton,
        FormTextarea,
        CategoryDimensionsSelector,
        CategoryDimensionsSelectorSummary,
        SelectAllNoneButtons,
    },
    layout: Layout,
    name: 'Edit',
    props: ['teacher', 'meta', 'options', 'evaluationCriterion'],
    data() {
        return {
            form: this.$inertia.form({
                title: this.evaluationCriterion.title,
                description: this.evaluationCriterion.description,
                stage_id: this.evaluationCriterion.stage_id,
                dimension_ids: this.evaluationCriterion.dimension_ids,
                grade_level_ids: this.evaluationCriterion.grade_level_ids,
            }),
        };
    },
    computed: {
        filteredGradeLevels() {
            if (!this.form.stage_id) {
                return [];
            }
            return this.options.gradeLevels.filter((s) => s.stage_id == this.form.stage_id);
        },
        filteredCategories() {
            if (!this.form.stage_id) {
                return [];
            }
            return this.options.categories.filter((c) => c.stage_id == this.form.stage_id);
        },
    },
    watch: {
        'form.stage_id': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.form.grade_level_ids = [];
                    this.form.dimension_ids = [];
                }
            },
            deep: true,
        },
    },
    methods: {
        submit() {
            this.form.patch(this.meta.links.update);
        },
        selectAllGradeLevels() {
            this.form.grade_level_ids = this.filteredGradeLevels.map((g) => g.value);
        },
        removeDimension(id) {
            const index = this.form.dimension_ids.indexOf(id);
            if (index !== -1) {
                this.form.dimension_ids.splice(index, 1);
            }
        },
    },
};
</script>
